/**
 * Contact Picker utility using the Contact Picker API
 * Falls back to manual entry if API not available
 */

// Check if Contact Picker API is available
// Note: iOS Safari 14.5+ supports navigator.contacts but does NOT expose
// ContactsManager globally, so check navigator.contacts.select instead.
export const isContactPickerSupported = () => {
  return 'contacts' in navigator && typeof navigator.contacts?.select === 'function';
};

/**
 * Open the native contact picker
 * @returns {Promise<Array>} Array of selected contacts with name and phone
 */
export const pickContacts = async (multiple = true) => {
  if (!isContactPickerSupported()) {
    throw new Error('Contact Picker API not supported');
  }

  try {
    const props = ['name', 'tel'];
    const opts = { multiple };
    
    const contacts = await navigator.contacts.select(props, opts);
    
    // Format contacts
    return contacts.map(contact => ({
      name: contact.name?.[0] || '',
      phone: formatPhoneNumber(contact.tel?.[0] || ''),
      rawPhone: contact.tel?.[0] || '',
    })).filter(c => c.phone); // Only return contacts with phone numbers
    
  } catch (error) {
    if (error.name === 'InvalidStateError') {
      throw new Error('Contact picker already open');
    }
    throw error;
  }
};

/**
 * Format phone number to 10 digits (Indian format)
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  let cleaned = phone.replace(/\D/g, '');
  
  // If starts with country code (91), remove it
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    cleaned = cleaned.substring(2);
  }
  
  // If starts with +91, already handled above
  // If starts with 0, remove it
  if (cleaned.startsWith('0') && cleaned.length === 11) {
    cleaned = cleaned.substring(1);
  }
  
  // Return only if valid 10-digit number
  if (cleaned.length === 10) {
    return cleaned;
  }
  
  return '';
};

/**
 * Format phone for display
 */
export const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const cleaned = formatPhoneNumber(phone);
  if (cleaned.length !== 10) return phone;
  return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
};

export default {
  isContactPickerSupported,
  pickContacts,
  formatPhoneNumber,
  formatPhoneDisplay,
};
